import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { Middleware as GenericMiddleware } from '@reduxjs/toolkit';
import cartItemReducer from './CartItem/reducer';
import {
  setProductAmountMiddleware,
  removeProductMiddleware,
  deleteGiftMessageMiddleware,
  addUpsellProductMiddleware,
  setGiftMessageMiddleware,
} from './CartItem/middleware';
import faqReducer from './FAQ/reducer';
import { metaDataReducer } from './MetaData/reducer';
import { pricesReducer } from './Prices/reducer';
import {
  addDiscountCodeMiddleware,
  recalculateAllPricesMiddleware,
  resetDiscountCodeMiddleware,
  resetVoucherCodeMiddleware,
} from './Prices/middleware';
import upsellReducer from './Upsell/reducer';
import {
  removeUpsellMiddleware,
  setUpsellAmountMiddleware,
} from './Upsell/middleware';
import recentlyViewedItemReducer from './RecentlyViewedItem/reducer';
import { translationReducer } from './Translation/reducer';
import { uiReducer } from './UI/reducer';
import contextDataReducer from './ContextData/reducer';

const rootReducer = combineReducers({
  cartItems: cartItemReducer,
  contextData: contextDataReducer,
  faqs: faqReducer,
  metaData: metaDataReducer,
  prices: pricesReducer,
  recentlyViewedItems: recentlyViewedItemReducer,
  translations: translationReducer,
  ui: uiReducer,
  upsells: upsellReducer,
});

type RootState = ReturnType<typeof rootReducer>;

const middleWares: GenericMiddleware[] = [
  deleteGiftMessageMiddleware,
  recalculateAllPricesMiddleware,
  removeProductMiddleware,
  addUpsellProductMiddleware,
  removeUpsellMiddleware,
  setProductAmountMiddleware,
  setUpsellAmountMiddleware,
  addDiscountCodeMiddleware,
  resetDiscountCodeMiddleware,
  resetVoucherCodeMiddleware,
  setGiftMessageMiddleware,
];

export const createStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    devTools: { name: 'checkout' },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(middleWares),
    preloadedState,
    reducer: rootReducer,
  });

type Store = ReturnType<typeof createStore>;
export type TypedDispatch = Store['dispatch'];
// as Recommended by Redux keep using {} https://redux.js.org/usage/usage-with-typescript#type-checking-middleware
// eslint-disable-next-line @typescript-eslint/ban-types
export type TypedMiddleware = GenericMiddleware<{}, RootState, TypedDispatch>;

export type { RootState, Store };
